import { Controller } from "@hotwired/stimulus";

// One player for the whole site, rendered by the layout on every page in that
// page's language. It is a full screen modal that stops when closed, so there
// is nothing to carry across a Turbo visit.
// Sample and full file are the same code path, only the source differs.
// Visual state is the theme's own: is-open on the modal, is-on on the music
// and effect toggles, is-playing on the main button, ambient-<key> on the
// background, and the progress ring driven by stroke-dashoffset.
const RING_LENGTH = 289.03;

export default class extends Controller {
  static targets = [
    "sampleLabel",
    "modal", "title", "cover", "ambient", "time", "duration", "seek", "playButton",
    "musicToggle", "volume", "volumeBubble", "effectButton", "sampleBar", "note",
    "downloadLink", "voice", "music"
  ];

  initialize() {
    this.effects = new Map();
    this.enabledEffects = new Set();
    this.musicOn = true;
    this.voiceTarget.addEventListener("timeupdate", () => this.tick());
    this.voiceTarget.addEventListener("loadedmetadata", () => this.tick());
    this.voiceTarget.addEventListener("ended", () => this.pause());
    this.voiceTarget.addEventListener("error", () => this.refreshFullUrl());
    this.voiceTarget.addEventListener("play", () => this.syncMusic());
    this.voiceTarget.addEventListener("pause", () => this.syncMusic());
    this.onKeydown = (e) => {
      if (e.key === "Escape" && this.modalTarget.classList.contains("is-open")) this.close();
    };
  }

  connect() {
    document.addEventListener("keydown", this.onKeydown);
  }

  // Turbo swaps in the next page's player; this one must fall silent, give
  // the scroll back and let go of its audio context.
  disconnect() {
    document.removeEventListener("keydown", this.onKeydown);
    this.close();
    // Effects are wired into this context and cannot be moved to another.
    this.resetEffects();
    this.effects = new Map();
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
  }

  // Fired by meditation_card_controller as a window event; detail is the card JSON.
  async open(event) {
    const meditation = event.detail;
    this.meditation = meditation;
    this.refreshes = 0;
    this.ensureAudioContext();
    this.titleTarget.textContent = meditation.title;
    this.coverTarget.style.backgroundImage = meditation.cover_url ? `url("${meditation.cover_url}")` : "none";
    // A free prayer's preview is the whole recording, so there is nothing to
    // fetch: asking the file endpoint only earns a redirect to the sign-in
    // page, which the player would report as a failure. There is still
    // something to sell, though, which is the rest of the catalogue.
    this.wholeRecording = meditation.preview_seconds === meditation.duration_seconds;
    // A free prayer carries a full_path for everyone, so that alone does not
    // mean the listener bought anything: only a paid prayer's path does.
    this.sampleBarTarget.hidden = Boolean(meditation.full_path) && !meditation.free;
    if (this.hasSampleLabelTarget) {
      const { sampleLabel, freeLabel } = this.element.dataset;
      this.sampleLabelTarget.textContent = this.wholeRecording ? freeLabel : sampleLabel;
    }
    this.downloadLinkTarget.hidden = !meditation.full_path;
    if (meditation.full_path) this.downloadLinkTarget.href = `${meditation.full_path}?download=1`;
    if (this.hasNoteTarget) { this.noteTarget.textContent = ""; this.noteTarget.hidden = true; }

    this.musicTarget.src = meditation.music_url || "";
    this.musicTarget.currentTime = 0;
    this.musicOn = Boolean(meditation.music_url);
    this.musicToggleTarget.classList.toggle("is-on", this.musicOn);
    this.musicToggleTarget.classList.toggle("is-disabled", !meditation.music_url);
    this.musicToggleTarget.setAttribute("aria-pressed", this.musicOn ? "true" : "false");
    this.setVolume();

    this.resetEffects();
    this.timeTarget.textContent = "0:00";
    this.durationTarget.textContent = "0:00";
    this.seekTarget.setAttribute("stroke-dashoffset", RING_LENGTH);

    let src = meditation.preview_url;
    if (meditation.full_path && !this.wholeRecording) {
      // An owner whose file endpoint errors still gets the sample rather than
      // a player that never opens.
      const full = await this.fetchFullUrl(meditation.full_path);
      if (full) {
        src = full;
      } else {
        // The sample bar stays hidden here on purpose. It sells the product,
        // and the person reading this owns it: what they need is the note
        // saying the file failed, not a buy button, whose anchor does not
        // even exist on the catalogue.
        this.downloadLinkTarget.hidden = true;
        this.showNote(this.element.dataset.fullUnavailable);
      }
    }
    if (!src) return;
    this.voiceTarget.src = src;
    this.voiceTarget.currentTime = 0;
    this.modalTarget.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.play();
    this.updateMediaSession();
  }

  close() {
    this.pause();
    this.stopMusicFade();
    this.musicTarget.pause();
    this.musicTarget.volume = this.musicLevel();
    this.voiceTarget.currentTime = 0;
    this.musicTarget.currentTime = 0;
    this.effects.forEach((audio) => { audio.currentTime = 0; });
    this.modalTarget.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  toggle() { this.voiceTarget.paused ? this.play() : this.pause(); }

  play() {
    this.voiceTarget.play().catch(() => {});
    this.playButtonTarget.classList.add("is-playing");
  }

  pause() {
    this.voiceTarget.pause();
    this.playButtonTarget.classList.remove("is-playing");
  }

  back() { this.voiceTarget.currentTime = Math.max(0, this.voiceTarget.currentTime - 5); }
  forward() { this.voiceTarget.currentTime = Math.min(this.voiceTarget.duration || 0, this.voiceTarget.currentTime + 5); }

  // The slider drives the background music only; voice and effects keep their
  // own fixed levels, as on the source page.
  setVolume() {
    const value = Number(this.volumeTarget.value);
    // Moving the slider mid-fade takes over the level at once, unless the
    // music is on its way out, which finishes on its own.
    if (this.musicFadeTo !== 0) this.stopMusicFade();
    this.musicTarget.volume = this.musicLevel();
    if (this.hasVolumeBubbleTarget) this.volumeBubbleTarget.textContent = `${value}%`;
    this.volumeTarget.parentElement?.style.setProperty("--val", `${value}%`);
    this.volumeTarget.parentElement?.parentElement?.classList.toggle("is-muted", value === 0);
  }

  toggleMusic() {
    if (this.musicToggleTarget.classList.contains("is-disabled")) return;
    this.musicOn = !this.musicOn;
    this.musicToggleTarget.classList.toggle("is-on", this.musicOn);
    this.musicToggleTarget.setAttribute("aria-pressed", this.musicOn ? "true" : "false");
    this.syncMusic();
  }

  async toggleEffect(event) {
    const button = event.currentTarget;
    const url = button.dataset.url;
    const on = !button.classList.contains("is-on");
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
    if (on) {
      // The click is a user gesture, which is the only moment the browser will
      // start a suspended context. Routing an element into a context that has
      // not started yet takes its sound off the normal output without putting
      // it anywhere else, so the effect plays silently.
      await this.ensureAudioContext();
      this.enabledEffects.add(url);
      let audio = this.effects.get(url);
      if (!audio) {
        audio = new Audio();
        // crossOrigin must be set before src, or the media element source
        // cannot be routed through the AudioContext (tainted stream).
        audio.crossOrigin = "anonymous";
        audio.loop = true;
        audio.src = url;
        this.effects.set(url, audio);
        this.routeThroughMixer(audio);
      }
      // An effect is a layer of its own: someone may want rain or chant with
      // the voice paused, so it starts on its own rather than waiting for one.
      audio.play().catch(() => {});
    } else {
      this.enabledEffects.delete(url);
      this.effects.get(url)?.pause();
    }
    this.updateAmbient();
  }

  // ---- internals ----

  showNote(text) {
    if (!this.hasNoteTarget || !text) return;
    this.noteTarget.textContent = text;
    this.noteTarget.hidden = false;
  }

  resetEffects() {
    this.effects.forEach((audio) => { audio.pause(); audio.currentTime = 0; });
    this.enabledEffects.clear();
    this.effectButtonTargets.forEach((button) => {
      button.classList.remove("is-on");
      button.setAttribute("aria-pressed", "false");
    });
    this.updateAmbient();
  }

  // The background takes the ambient scene of the first effect switched on.
  // Only these keys have a scene in the theme CSS; anything else (and no
  // effect at all) keeps the neutral "brak" background.
  updateAmbient() {
    if (!this.hasAmbientTarget) return;
    const scenes = ["deszcz", "ogien", "fale", "las"];
    const active = this.effectButtonTargets.find(
      (button) => button.classList.contains("is-on") && scenes.includes(button.dataset.fx)
    );
    const key = active ? active.dataset.fx : "brak";
    this.ambientTarget.className = `player-bg-ambient ambient-${key}`;
  }

  tick() {
    const { currentTime, duration } = this.voiceTarget;
    this.timeTarget.textContent = this.format(currentTime);
    this.durationTarget.textContent = this.format(duration);
    if (duration) {
      this.seekTarget.setAttribute("stroke-dashoffset", RING_LENGTH * (1 - currentTime / duration));
    }
    if ("mediaSession" in navigator && duration) {
      navigator.mediaSession.setPositionState({ duration, position: currentTime, playbackRate: 1 });
    }
  }

  // The music comes in a second after the voice on a fresh start, so the
  // opening words are heard alone, and swells in rather than jumping to the
  // slider level; resuming after a pause is immediate with a shorter swell.
  MUSIC_DELAY_MS = 1000;
  MUSIC_FADE_IN_MS = 4000;
  MUSIC_RESUME_FADE_MS = 2000;
  MUSIC_FADE_OUT_MS = 1000;
  MUSIC_FADE_STEP_MS = 100;

  syncMusic() {
    clearTimeout(this.musicDelay);
    const wantMusic = this.musicOn && !this.voiceTarget.paused && this.musicTarget.src;
    if (!wantMusic) {
      this.stopMusic();
    } else if (this.voiceTarget.currentTime < 0.5) {
      this.musicDelay = setTimeout(() => {
        if (this.musicOn && !this.voiceTarget.paused) this.startMusic(this.MUSIC_FADE_IN_MS);
      }, this.MUSIC_DELAY_MS);
    } else {
      this.startMusic(this.MUSIC_RESUME_FADE_MS);
    }
    if (this.voiceTarget.paused) {
      this.effects.forEach((audio) => audio.pause());
    } else {
      this.effects.forEach((audio, url) => { if (this.enabledEffects.has(url)) audio.play().catch(() => {}); });
    }
  }

  musicLevel() { return Math.min(1, Number(this.volumeTarget.value) / 100); }

  startMusic(fadeMs) {
    if (!this.musicTarget.paused && this.musicFadeTo !== 0) return;
    if (this.musicTarget.paused) this.musicTarget.volume = 0;
    this.musicTarget.play().catch(() => {});
    this.fadeMusic(this.musicLevel(), fadeMs);
  }

  stopMusic() {
    if (this.musicTarget.paused) { this.stopMusicFade(); return; }
    this.fadeMusic(0, this.MUSIC_FADE_OUT_MS, () => {
      this.musicTarget.pause();
      this.musicTarget.volume = this.musicLevel();
    });
  }

  // Progress comes from the clock, not the tick count: a background tab
  // throttles timers to once a second, which must not stretch a 4s swell
  // into 40s.
  fadeMusic(to, ms, done) {
    this.stopMusicFade();
    const from = this.musicTarget.volume;
    const started = performance.now();
    this.musicFadeTo = to;
    this.musicFade = setInterval(() => {
      const progress = Math.min(1, (performance.now() - started) / ms);
      this.musicTarget.volume = from + (to - from) * progress;
      if (progress >= 1) { this.stopMusicFade(); done?.(); }
    }, this.MUSIC_FADE_STEP_MS);
  }

  stopMusicFade() {
    clearInterval(this.musicFade);
    this.musicFade = null;
    this.musicFadeTo = null;
  }

  async fetchFullUrl(path) {
    const response = await fetch(path, { headers: { Accept: "application/json" }, credentials: "same-origin" });
    if (!response.ok) return null;
    return (await response.json()).url;
  }

  // Presigned URLs live two hours; a stalled <audio> after that gets a new one.
  // Bounded to avoid looping forever if the source keeps failing.
  async refreshFullUrl() {
    if (!this.meditation?.full_path || this.refreshes >= 2) return;
    this.refreshes += 1;
    const at = this.voiceTarget.currentTime;
    const url = await this.fetchFullUrl(this.meditation.full_path);
    if (!url) return;
    this.voiceTarget.src = url;
    this.voiceTarget.currentTime = at;
    this.play();
  }

  // iOS Safari only starts audio inside a user gesture; "open" is one, so the
  // context is created (and resumed) here and reused for every effect.
  async ensureAudioContext() {
    if (!this.audioContext && window.AudioContext) this.audioContext = new AudioContext();
    // resume() is a promise, and routing an element into a context that is
    // still suspended loses its sound, so callers wait for it to settle.
    await this.audioContext?.resume().catch(() => {});
  }

  routeThroughMixer(audio) {
    if (!this.audioContext) return;
    const source = this.audioContext.createMediaElementSource(audio);
    const gain = this.audioContext.createGain();
    gain.gain.value = 0.8;
    source.connect(gain).connect(this.audioContext.destination);
  }

  updateMediaSession() {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.meditation.title,
      artist: "Oremus",
      artwork: this.meditation.cover_url ? [{ src: this.meditation.cover_url, sizes: "512x512", type: "image/jpeg" }] : []
    });
    navigator.mediaSession.setActionHandler("play", () => this.play());
    navigator.mediaSession.setActionHandler("pause", () => this.pause());
    navigator.mediaSession.setActionHandler("seekbackward", () => this.back());
    navigator.mediaSession.setActionHandler("seekforward", () => this.forward());
  }

  format(seconds) {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }
}
